import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import { ApplyHeldBack } from "@common/utils/applyOutcome";
import { ApplyFailureError } from "./errors";
import { ProposalEngineAccess } from "./proposal-engine-access";

export const REPLY_TARGET_RESOLVED_MESSAGE = "The comment this replies to was resolved.";
export const REPLY_TARGET_DELETED_MESSAGE = "The comment this replies to was deleted.";

const commentWithId = async (context: Word.RequestContext, commentId: string, loadedProperties: string): Promise<Word.Comment | null> => {
    const comments = context.document.body.getComments();
    context.load(comments, loadedProperties);
    await context.sync();
    return comments.items.find((comment) => String(comment.id) === String(commentId)) ?? null;
};

export const applyCommentReply = async (
    context: Word.RequestContext,
    targetCommentId: string,
    replyText: string,
): Promise<void> => {
    const proposalEngineEnabled = (await ProposalEngineAccess.current()).enabled;
    const targetComment = await commentWithId(context, targetCommentId, proposalEngineEnabled ? "items/id,items/resolved" : "items/id");
    if (targetComment === null) {
        if (proposalEngineEnabled) throw new ApplyHeldBack(REPLY_TARGET_DELETED_MESSAGE);
        throw new ApplyFailureError(
            "We can't find the comment to reply to. It may have been deleted or resolved since the reply was generated. You may need to regenerate the reply.",
            APPLY_FAILURE_LOCATION_NOT_FOUND,
        );
    }
    if (proposalEngineEnabled && targetComment.resolved) throw new ApplyHeldBack(REPLY_TARGET_RESOLVED_MESSAGE);

    targetComment.reply(replyText);
    await context.sync();
};

export const commentContentOf = async (context: Word.RequestContext, commentId: string): Promise<string | null> => {
    const comment = await commentWithId(context, commentId, "items/id,items/content");
    return comment === null ? null : comment.content;
};

export const selectCommentRange = async (context: Word.RequestContext, commentId: string): Promise<boolean> => {
    const comment = await commentWithId(context, commentId, "items/id");
    if (comment === null) return false;
    comment.getRange().select();
    await context.sync();
    return true;
};
